/**
 * 공유받은 글과 이미지를 꺼낸다.
 *
 * 서비스 워커(public/share-target-sw.js)가 받은 것을 기기 캐시에 넣어 두고
 * /share?id=… 로 보낸다. 캐시 이름과 주소 모양은 그쪽과 같아야 한다.
 * 서비스 워커가 없을 때는 서버(api/share-target.js)가 글만 주소에 실어 보낸다 (?text=…).
 */

export const SHARE_CACHE = 'share-inbox';

const entryUrl = (id, part) => `/__share/${id}/${part}`;
// 주소로 들어온 id 라 모양을 확인한다 (서비스 워커가 만드는 것은 영소문자와 숫자뿐)
const isShareId = (id) => typeof id === 'string' && /^[a-z0-9]{4,32}$/.test(id);

const hasCaches = () => typeof caches !== 'undefined';

// 폼으로 보낸 글은 줄바꿈이 \r\n 으로 바뀌어 온다 (multipart 전송 규칙). 화면에서는 \n 하나로 쓴다
const lines = (value) => String(value || '').replace(/\r\n?/g, '\n');

/**
 * 주소의 정보로 받은 공유를 꺼낸다.
 * @param {URLSearchParams} params
 * @returns {Promise<{ title, text, url, image: Blob|null, droppedImage: boolean, error: string }|null>}
 *   꺼낼 것이 없으면 null
 */
export const readSharedPayload = async (params) => {
  const error = params.get('error') || '';
  const id = params.get('id');

  if (isShareId(id) && hasCaches()) {
    const cache = await caches.open(SHARE_CACHE);
    const fieldsResponse = await cache.match(entryUrl(id, 'fields'));
    if (!fieldsResponse) return error ? { title: '', text: '', url: '', image: null, droppedImage: false, error } : null;

    const fields = await fieldsResponse.json();
    const imageResponse = fields.hasImage ? await cache.match(entryUrl(id, 'image')) : null;
    return {
      title: lines(fields.title),
      text: lines(fields.text),
      url: fields.url || '',
      image: imageResponse ? await imageResponse.blob() : null,
      droppedImage: false,
      error
    };
  }

  // 서버가 주소에 실어 보낸 경우
  const title = lines(params.get('title'));
  const text = lines(params.get('text'));
  const url = params.get('url') || '';
  const droppedImage = params.get('dropped') === 'image';
  if (!title && !text && !url && !droppedImage && !error) return null;
  return { title, text, url, image: null, droppedImage, error };
};

/** 다 쓴 공유를 지운다 */
export const discardSharedPayload = async () => {
  if (!hasCaches()) return;
  try {
    const cache = await caches.open(SHARE_CACHE);
    const keys = await cache.keys();
    await Promise.all(keys.map(key => cache.delete(key)));
  } catch { /* 지우지 못해도 다음 공유가 덮어쓴다 */ }
};

/*
 * 공유 화면에서 고치던 내용. 쪽수를 확인하러 전자책에 다녀오는 사이 앱이 꺼져도 남게 한다.
 * source 는 어느 공유에 대한 초안인지 — 다른 공유가 오면 쓰지 않는다.
 */
const DRAFT_KEY = 'br_share_draft';

export const loadShareDraft = (source) => {
  try {
    const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    return draft && draft.source === source ? draft : null;
  } catch {
    return null;
  }
};

export const saveShareDraft = (draft) => {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); }
  catch { /* 저장 실패는 무시 */ }
};

export const clearShareDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); }
  catch { /* 무시 */ }
};
